import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiSave, 
  FiX, 
  FiAlertCircle
} from 'react-icons/fi';
import api from '../../services/api';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.75rem, 2.5vw + 1rem, 2.5rem);
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const AddButton = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  touch-action: manipulation;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem;
    font-size: 0.875rem;
  }
`;

const PromoCodesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PromoCodeCard = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color);
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CardInfo = styled.div`
  flex: 1;
`;

const PromoCode = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
  font-family: 'Courier New', monospace;
`;

const Description = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
`;

const DiscountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const DiscountValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'edit' ? 'var(--primary-color)' : props.variant === 'delete' ? 'var(--error-color)' : 'var(--background-secondary)'};
  color: ${props => props.variant === 'delete' ? 'white' : 'var(--text-primary)'};
  border: none;
  border-radius: 0;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.variant === 'edit' ? 'var(--primary-hover)' : props.variant === 'delete' ? 'var(--error-dark)' : 'var(--border-color)'};
    transform: translateY(-1px);
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span`
  background: ${props => props.active ? 'var(--success-color)' : 'var(--error-color)'};
  color: var(--text-inverse);
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-primary);
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' && `
    background: var(--primary-color);
    color: var(--text-inverse);
    border: none;

    &:hover {
      background: var(--primary-hover);
      transform: translateY(-1px);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: var(--background-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--border-color);
    }
  `}
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: rgba(252, 129, 129, 0.1);
  color: var(--error-color);
  padding: 1rem;
  border-radius: 0;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

function PromoCodeManagement() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_course_count: 1,
    max_uses: '',
    start_date: '',
    end_date: '',
    is_active: true,
    applicable_courses: [],
    applicable_products: [],
    apply_to_all_courses: false,
    apply_to_all_products: false,
    success_message: ''
  });

  useEffect(() => {
    fetchPromoCodes();
    fetchCoursesAndProducts();
  }, []);

  const fetchCoursesAndProducts = async () => {
    try {
      const [coursesResponse, productsResponse] = await Promise.all([
        api.getCourses(),
        api.getAdditionalProducts()
      ]);
      setCourses(coursesResponse.courses || []);
      setProducts(productsResponse.products || []);
    } catch (error) {
      console.error('Error fetching courses and products:', error);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await api.getPromoCodes();
      setPromoCodes(response.promoCodes);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      setError('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingPromo(null);
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_course_count: 1,
      max_uses: '',
      start_date: '',
      end_date: '',
      is_active: true,
      applicable_courses: [],
      applicable_products: [],
      apply_to_all_courses: false,
      apply_to_all_products: false,
      success_message: ''
    });
    setShowModal(true);
  };

  const handleEdit = (promo) => {
    console.log('Editing promo code:', promo);
    setEditingPromo(promo);
    
    // Parse JSON fields safely
    let applicableCourses = [];
    let applicableProducts = [];
    
    try {
      if (promo.applicable_courses) {
        applicableCourses = JSON.parse(promo.applicable_courses);
      }
    } catch (e) {
      console.error('Error parsing applicable_courses:', e);
    }
    
    try {
      if (promo.applicable_products) {
        applicableProducts = JSON.parse(promo.applicable_products);
      }
    } catch (e) {
      console.error('Error parsing applicable_products:', e);
    }
    
    const formDataToSet = {
      code: promo.code,
      description: promo.description || '',
      discount_type: promo.discount_type,
      discount_value: promo.discount_value,
      min_course_count: promo.min_course_count,
      max_uses: promo.max_uses || '',
      start_date: promo.start_date ? promo.start_date.split('T')[0] : '',
      end_date: promo.end_date ? promo.end_date.split('T')[0] : '',
      is_active: promo.is_active,
      applicable_courses: applicableCourses,
      applicable_products: applicableProducts,
      apply_to_all_courses: Boolean(promo.apply_to_all_courses),
      apply_to_all_products: Boolean(promo.apply_to_all_products),
      success_message: promo.success_message || ''
    };
    console.log('Setting form data:', formDataToSet);
    setFormData(formDataToSet);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this promo code?')) {
      try {
        await api.deletePromoCode(id);
        await fetchPromoCodes();
      } catch (error) {
        console.error('Error deleting promo code:', error);
        setError('Failed to delete promo code');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        min_course_count: parseInt(formData.min_course_count),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      console.log('Submitting promo code:', { editingPromo, submitData });

      if (editingPromo) {
        console.log('Updating promo code with ID:', editingPromo.id);
        await api.updatePromoCode(editingPromo.id, submitData);
        console.log('Update successful');
      } else {
        console.log('Creating new promo code');
        await api.createPromoCode(submitData);
        console.log('Create successful');
      }

      setShowModal(false);
      await fetchPromoCodes();
    } catch (error) {
      console.error('Error saving promo code:', error);
      setError('Failed to save promo code');
    }
  };

  const handleCourseSelection = (courseId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        applicable_courses: [...prev.applicable_courses, parseInt(courseId)]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        applicable_courses: prev.applicable_courses.filter(id => id !== parseInt(courseId))
      }));
    }
  };

  const handleProductSelection = (productId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        applicable_products: [...prev.applicable_products, parseInt(productId)]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        applicable_products: prev.applicable_products.filter(id => id !== parseInt(productId))
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No limit';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDiscount = (type, value) => {
    // Ensure value is a number and remove any existing symbols
    const numericValue = parseFloat(value);
    if (type === 'percentage') {
      return `${numericValue}%`;
    } else {
      return `$${numericValue.toFixed(2)}`;
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading promo codes...</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Promo Code Management</Title>
        <AddButton onClick={handleAddNew}>
          <FiPlus size={16} />
          Add Promo Code
        </AddButton>
      </Header>

      {error && (
        <ErrorMessage>
          <FiAlertCircle size={16} />
          {error}
        </ErrorMessage>
      )}

      <PromoCodesGrid>
        {promoCodes.map((promo) => (
          <PromoCodeCard
            key={promo.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CardHeader>
              <CardInfo>
                <PromoCode>{promo.code}</PromoCode>
                {promo.description && <Description>{promo.description}</Description>}
                <DiscountInfo>
                  <DiscountValue>
                    {formatDiscount(promo.discount_type, promo.discount_value)}
                  </DiscountValue>
                </DiscountInfo>
              </CardInfo>
              <CardActions>
                <ActionButton variant="edit" onClick={() => handleEdit(promo)}>
                  <FiEdit size={16} />
                </ActionButton>
                <ActionButton variant="delete" onClick={() => handleDelete(promo.id)}>
                  <FiTrash2 size={16} />
                </ActionButton>
              </CardActions>
            </CardHeader>
            <CardBody>
              <InfoRow>
                <InfoLabel>Status</InfoLabel>
                <StatusBadge active={promo.is_active}>
                  {promo.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Min Courses</InfoLabel>
                <InfoValue>{promo.min_course_count}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Usage</InfoLabel>
                <InfoValue>
                  {promo.used_count} / {promo.max_uses || '∞'}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Valid Until</InfoLabel>
                <InfoValue>{formatDate(promo.end_date)}</InfoValue>
              </InfoRow>
            </CardBody>
          </PromoCodeCard>
        ))}
      </PromoCodesGrid>

      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <ModalHeader>
              <ModalTitle>
                {editingPromo ? 'Edit Promo Code' : 'Add New Promo Code'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Promo Code *</Label>
                <Input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., WELCOME10"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this promo code..."
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="success_message">Success Message (shown when promo code is applied)</Label>
                <Input
                  id="success_message"
                  type="text"
                  value={formData.success_message}
                  onChange={(e) => setFormData({ ...formData, success_message: e.target.value })}
                  placeholder="e.g., Thank you for using this promo code!"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>Discount Type *</Label>
                  <Select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    required
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed_amount">Fixed Amount</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Discount Value *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    placeholder={formData.discount_type === 'percentage' ? '10' : '25.00'}
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Min Course Count</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.min_course_count}
                    onChange={(e) => setFormData({ ...formData, min_course_count: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Max Uses (optional)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    placeholder="Leave empty for unlimited"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Start Date (optional)</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>End Date (optional)</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Applicable Courses</Label>
                <div style={{ marginBottom: '1rem' }}>
                  <Label>
                    <input
                      type="checkbox"
                      checked={formData.apply_to_all_courses}
                      onChange={(e) => setFormData({ ...formData, apply_to_all_courses: e.target.checked })}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Apply to all courses
                  </Label>
                </div>
                {!formData.apply_to_all_courses && (
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color)', padding: '0.5rem', borderRadius: 0}}>
                    {courses.map(course => (
                      <div key={course.id} style={{ marginBottom: '0.5rem' }}>
                        <Label>
                          <input
                            type="checkbox"
                            checked={formData.applicable_courses.includes(course.id)}
                            onChange={(e) => handleCourseSelection(course.id, e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          {course.name} ({course.code})
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Applicable Products</Label>
                <div style={{ marginBottom: '1rem' }}>
                  <Label>
                    <input
                      type="checkbox"
                      checked={formData.apply_to_all_products}
                      onChange={(e) => setFormData({ ...formData, apply_to_all_products: e.target.checked })}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Apply to all products
                  </Label>
                </div>
                {!formData.apply_to_all_products && (
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color)', padding: '0.5rem', borderRadius: 0}}>
                    {products.map(product => (
                      <div key={product.id} style={{ marginBottom: '0.5rem' }}>
                        <Label>
                          <input
                            type="checkbox"
                            checked={formData.applicable_products.includes(product.id)}
                            onChange={(e) => handleProductSelection(product.id, e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          {product.name} (${product.price})
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Active
                </Label>
              </FormGroup>

              <ModalActions>
                <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  <FiSave size={16} />
                  {editingPromo ? 'Update' : 'Create'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default PromoCodeManagement;
